<template>
    <teleport to="#portal-target-popups" :order="order" v-if="isOpen">
        <div ref="popup" :class="['popup', popupClass]" :role="ariaType" :aria-labelledby="titleElementId" :aria-describedby="descriptionElementId">
            <div class="popup-dialog" :class="dialogClasses" ref="dialog">
                <div @focus="focusLastElement" tabindex="0" class="popup-focus-trap"/>
                <div class="popup-content" ref="popupContainer">
                    <div v-if="closeable || _titleIcon || _title" class="popup-headline">
                        <div class="popup-headline-wrapper">
                            <div class="headline-text text-overflow" :class="{'has-icon': _titleIcon}">
                                <i class="material-icons" v-if="_titleIcon" aria-hidden="true" role="img">{{_titleIcon}}</i>
                                <slot name="title" v-bind="{titleElementId}"><span :id="titleElementId" v-html="_title"></span></slot>
                            </div>
                            <div class="pointer" v-if="closeable || currentUser.isImpersonate" v-interact="(event: MouseEvent | KeyboardEvent) => close(event, 'aborted')">
                                <material-icon icon="close" :size="20" class="popup-close-icon"/>
                            </div>
                        </div>
                    </div>
                    <component class="popup-body" :is="bodyComponent" @submit.prevent="submit" tabindex="-1" ref="popupBody"
                        :class="{
                        'overflow-y-scroll': _alwaysShowScrollBar,
                        'padding-top-15': viewSize.smDown && fullWidthOnMobile,
                        'padding-15': !(viewSize.smDown && fullWidthOnMobile)
                    }">
                        <div :class="{'row flex-vertical-middle': _textIcon}">
                            <div v-if="_textIcon" class="hidden-xs" :class="textIconColCLass">
                                <i class="material-icons md-86" :class="_textIconClass" aria-hidden="true" role="img">{{_textIcon}}</i>
                            </div>

                            <div :class="textColClass" :id="descriptionElementId">
                                <component v-if="component" :is="component" v-bind="componentProps" @set-buttons="setButtons"/>
                                <slot v-else>
                                    <div v-html="text"></div>
                                </slot>
                            </div>
                        </div>
                    </component>
                    <div class="popup-footer button-container" :class="{flex: flexButtonRow}" v-if="_buttons.length">
                        <template v-for="button in _buttons">
                            <div v-if="'spacer' === button.title" class="flex-grow">&nbsp;</div>
                            <button v-else-if="button.cmd" class="button" @click.stop="buttonClick(button.disabled ? null : button.cmd, $event)"
                                :class="[{disabled: button.disabled}, 'button-' + (button.type || 'default'), button.class || []]"
                                v-tooltip="button.tooltip || ''">
                                <i class="material-icons md-16" v-if="button.icon" aria-hidden="true" role="img">{{button.icon}}</i> {{button.title}}
                            </button>
                            <router-link v-else-if="button.to" :to="button.to" :target="'_' + (button.target || 'self')" class="button"
                                :class="[{disabled: button.disabled}, 'button-' + (button.type || 'default'), button.class || []]"
                                v-tooltip="button.tooltip || ''" @click="close">
                                <i class="material-icons md-16" v-if="button.icon" aria-hidden="true" role="img">{{button.icon}}</i> {{button.title}}
                            </router-link>
                            <app-popover v-if="button.children" position="bottom-left" class="inline-block default-margin">
                                <template #default="{toggle}">
                                    <div class="button button-primary no-margin flex dropdown-indicator-button" @click="toggle">
                                        <span><i class="material-icons md-18" aria-hidden="true" role="img">{{button.icon}}</i></span><span>{{button.title}}</span>
                                        <span><i class="material-icons md-18" aria-hidden="true" role="img">keyboard_arrow_down</i></span>
                                    </div>
                                </template>

                                <template #container>
                                    <ul class="portuna-menu">
                                        <li v-for="childButton in button.children">
                                            <button v-if="childButton.cmd" class="button no-margin full-width text-left no-border"
                                                @click.stop="buttonClick(childButton.disabled ? null : childButton.cmd, $event)"
                                                :class="[{disabled: childButton.disabled}, 'button-' + (childButton.type || 'default'), childButton.class || []]"
                                                v-tooltip="childButton.tooltip || ''">
                                                <i class="material-icons md-16" v-if="childButton.icon" aria-hidden="true" role="img">{{childButton.icon}}</i> {{childButton.title}}
                                            </button>
                                            <router-link v-else-if="childButton.to" :to="childButton.to" :target="'_' + (button.target || 'self')"
                                                class="button no-margin full-width text-left no-border"
                                                :class="[{disabled: childButton.disabled}, 'button-' + (childButton.type || 'default'), childButton.class || []]"
                                                v-tooltip="childButton.tooltip || ''" @click="close">
                                                <i class="material-icons md-16" v-if="childButton.icon" aria-hidden="true" role="img">{{childButton.icon}}</i> {{childButton.title}}
                                            </router-link>
                                        </li>
                                    </ul>
                                </template>
                            </app-popover>
                        </template>
                    </div>
                </div>
                <div @focus="focusFirstElement" tabindex="0" class="popup-focus-trap"/>
            </div>
        </div>
    </teleport>
</template>

<script setup lang="ts">
    import AppPopover from '@/components/AppPopover.vue';
    import {usePortuna} from '@/composables/portuna';
    import MaterialIcon from '@/components/MaterialIcon.vue';
    import {captureSilentError} from '@/library/sentry';
    import {TinyEmitter} from 'tiny-emitter';
    import {queryFocusableElements as queryFocusElements} from '../library/focus';
    import MediaQueryHandler, {
        addMediaQueryHandlers,
        getSizeClass,
    } from '../library/mediaQueryHandler';
    import {getCountOfAllOpenPopups, popupDefaultCloseButton, popUpInstances} from '@/library/popup';
    import {computed, type Ref, ref, reactive, nextTick, onUpdated, onUnmounted, watch, provide} from 'vue';
    import {useStore} from 'vuex';

    const portuna     = usePortuna();
    const currentUser = portuna.currentUser;

    const emit = defineEmits<{
        'update:modelValue': [true: boolean];
        submit: [...args: unknown[]];
        closePopup: [];
    }>();

    const props = withDefaults(
        defineProps<{
            modelValue?: boolean;
            title?: string;
            text?: string;
            closeable?: boolean;
            draggable?: boolean;
            resizable?: boolean;
            persistent?: boolean;
            flexButtonRow?: boolean;
            fullWidthOnMobile?: boolean;
            size?: PopupSize;
            form?: boolean;
            buttons?: PopupButton[];
            textIcon?: string;
            textIconPosition?: string;
            titleIcon?: string;
            textIconClass?: VueCssClass;
            popupClass?: VueCssClass;
            position?: 'center' | 'left' | 'right';
            height?: 'full' | 'default';
            ariaType?: 'dialog' | 'alertdialog';
            closeOnClickOutside?: boolean;
            customScrollBar?: boolean;
            alwaysShowScrollBar?: boolean;
            onClose?: Function;
            forceFocusLastElement?: boolean;
            closeAnimation?: boolean;
            animationTarget?: Record<string, any>;
            registerPopupInstance?: (app: any) => void;
            component?: any;
            componentProps?: Record<string, any>;
            events?: Record<string, AppPopupEventHandler>;
            provideEventHandler?: boolean
        }>(),
        {
            buttons: () => ([popupDefaultCloseButton]),
            modelValue: false,
            title: '',
            text: '',
            closeable: true,
            draggable: true,
            resizable: true,
            persistent: false,
            flexButtonRow: false,
            fullWidthOnMobile: false,
            size: 'long',
            form: false,
            textIcon: '',
            textIconPosition: 'left',
            titleIcon: '',
            textIconClass: '',
            popupClass: '',
            position: 'center',
            height: 'default',
            ariaType: 'dialog',
            closeOnClickOutside: false,
            customScrollBar: false,
            alwaysShowScrollBar: false,
            onClose: undefined,
            forceFocusLastElement: false,
            closeAnimation: false,
            animationTarget: () => ({}),
            registerPopupInstance: () => ({}),
            component: null,
            provideEventHandler: false,
            componentProps: () => ({}),
            events: () => ({}),
        },
    );

    const store = useStore();

    const viewSize = store.state.viewSize;

    const popup          = ref<HTMLDivElement>();
    const popupContainer = ref<HTMLElement>();
    const popupBody      = ref<HTMLElement>();

    const _alwaysShowScrollBar   = ref<boolean>(false);
    const _forceFocusLastElement = ref<boolean>(false);
    const _buttons               = ref<PopupButton[]>([]);
    const _size                  = ref<PopupSize>('long');
    const _title                 = ref<string>('');
    const _titleIcon             = ref<string>('');
    const _textIcon              = ref<string>('');
    const _textIconClass         = ref<VueCssClass>('');
    const opened                 = ref<boolean>(false);
    const order                  = ref(0);
    const pageFocus              = ref<Nullable<HTMLElement>>(null);
    const focusableElements      = ref<Nullable<NodeListOf<HTMLElement>>>(null);
    const uIds                   = ref<string[]>([]);
    const sizes                  = reactive({
        xs: false,
        sm: false,
        md: false,
        lg: false,
        xl: false,
        xxl: false,
    });

    const titleElementId = computed(() =>
    {
        return `dialog_${portuna._uid}_title`;
    });

    const descriptionElementId = computed(() =>
    {
        return `dialog_${portuna._uid}_desc`;
    });

    const positionClass = computed(() =>
    {
        return props.position === 'right' ? 'popup-right' : props.position === 'left' ? 'popup-left' : '';
    });

    const sizeClass = computed(() =>
    {
        return _size.value !== 'long' ? 'popup-' + _size.value : '';
    });

    const heightClass = computed(() =>
    {
        return props.height !== 'default' ? 'popup-' + props.height : '';
    });

    const isOpen = computed(() =>
    {
        let isOpen = props.modelValue || opened.value;
        if (isOpen)
        {
            order.value = Date.now();
            // document.documentElement.classList.add('disable-scroll');
        }

        return isOpen;
    });

    const textIconColCLass = computed((): string =>
    {
        let iconClass = '';
        if (_textIcon.value)
        {
            if (props.textIconPosition === 'top')
            {
                iconClass = 'col-12 text-center';
            }
            else if (props.textIconPosition === 'left')
            {
                if (_size.value === 'small')
                {
                    iconClass = 'col-3 text-right';
                }
                else if (_size.value === 'medium')
                {
                    iconClass = 'col-2 text-right';
                }
                else if (_size.value === 'long')
                {
                    iconClass = 'col-1 text-right';
                }
            }
        }

        return iconClass;
    });

    const textColClass = computed((): string =>
    {
        let textClass = '';
        if (_textIcon.value)
        {
            textClass = 'col-12 ';

            if (props.textIconPosition === 'left')
            {
                if (_size.value === 'small')
                {
                    textClass += 'col-sm-9';
                }
                else if (_size.value === 'medium')
                {
                    textClass += 'col-sm-10';
                }
                else if (_size.value === 'long')
                {
                    textClass += 'col-sm-11';
                }
            }
        }

        return textClass;
    });

    const bodyComponent = computed(() =>
    {
        return props.form ? 'form' : props.customScrollBar ? 'app-scrollbar' : 'div';
    });

    const dialogClasses = computed(() =>
    {
        return [sizeClass.value, positionClass.value, heightClass.value, getSizeClass(sizes)];
    });

    const submit = (event: Event) =>
    {
        portuna.emitter.emit('submit', event);
    };

    const popupIndex = ref<Nullable<number>>(null);

    const buttonClick = (buttonCmd: Nullable<PopupButtonCallback>, event: MouseEvent) =>
    {
        if (typeof buttonCmd === 'function')
        {
            buttonCmd(exposed, event);
        }
    };

    const setButtons = (buttons: PopupButton[]) =>
    {
        _buttons.value = buttons;
    };

    const removeResizeListeners = () =>
    {
        MediaQueryHandler.removeAllByIds(uIds.value);

        uIds.value = [];
    };

    const focusFirstElement = () =>
    {
        if (focusableElements.value && focusableElements.value[0])
        {
            focusableElements.value[0].focus();
        }
    };

    const focusLastElement = () =>
    {
        if (focusableElements.value && focusableElements.value[focusableElements.value.length - 1])
        {
            focusableElements.value[focusableElements.value.length - 1].focus();
        }
    };

    const queryFocusableElements = () =>
    {
        if (popup.value)
        {
            focusableElements.value = queryFocusElements(popup.value);
        }
    };

    const scrollTop = () =>
    {
        nextTick(() =>
        {
            popupBody.value?.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        });
    };

    const scrollBottom = () =>
    {
        nextTick(() =>
        {
            popupBody.value?.scrollTo({
                top: popupBody.value.offsetHeight,
                behavior: 'smooth',
            });
        });
    };

    const open = (): Promise<void> =>
    {
        if (opened.value)
        {
            return Promise.resolve();
        }

        opened.value = true;

        popupIndex.value = Number(getCountOfAllOpenPopups());

        if (props.closeOnClickOutside)
        {
            setTimeout(() =>
            {
                window.addEventListener('click', closeOnClickEvent);
            }, 1);
        }

        window.addEventListener('keyup', keyupListener);

        document.documentElement.classList.add('disable-scroll');

        pageFocus.value = document.activeElement as HTMLElement;

        emit('update:modelValue', true);

        return new Promise((resolve) =>
        {
            nextTick(() =>
            {
                queryFocusableElements();

                nextTick(() =>
                {
                    if (_forceFocusLastElement.value)
                    {
                        focusLastElement();
                    }
                    else
                    {
                        focusFirstElement();
                    }
                });

                resolve();
            });
        });
    };

    const close = (event?: MouseEvent | KeyboardEvent, reason: string = 'unknown') =>
    {
        if (!opened.value)
        {
            return;
        }

        if (props.closeOnClickOutside)
        {
            window.removeEventListener('click', closeOnClickEvent);
        }

        const closeCallback = () =>
        {
            opened.value = false;

            emit('update:modelValue', false);

            portuna.emitter.emit('close', event || null, reason);

            emit('closePopup');

            window.removeEventListener('keyup', keyupListener);

            pageFocus.value && pageFocus.value.focus();

            queryFocusableElements();

            props.onClose && props.onClose();
        };

        if (props.closeAnimation)
        {
            const {
                      x,
                      y,
                  } = popupContainer.value!.getBoundingClientRect();

            const translateX = props.animationTarget.x - x;
            const translateY = props.animationTarget.y - y;

            const Animation = popupContainer.value!.animate(
                [
                    {
                        transform: `translate(${translateX}px, ${translateY}px) scale(0)`,
                    },
                ],
                1000,
            );

            Animation.onfinish = closeCallback;
        }
        else
        {
            closeCallback();
        }
    };

    const keyupListener = (event: KeyboardEvent) =>
    {
        if (event.key === 'Escape' && props.closeable && popupIndex.value === getCountOfAllOpenPopups())
        {
            close(event, 'aborted');
        }
    };

    const closeOnClickEvent = (event: MouseEvent) =>
    {
        let target = event.target as HTMLElement;
        if (target === popup.value)
        {
            close();
        }
    };

    onUpdated(() =>
    {
        nextTick(() =>
        {
            if (opened.value)
            {
                queryFocusableElements();
            }
        });
    });

    _alwaysShowScrollBar.value   = props.alwaysShowScrollBar;
    _buttons.value               = props.buttons;
    _size.value                  = props.size;
    _title.value                 = props.title;
    _titleIcon.value             = props.titleIcon;
    _textIcon.value              = props.textIcon;
    _textIconClass.value         = props.textIconClass;
    _forceFocusLastElement.value = props.forceFocusLastElement;

    function overwriteOptions(options: Partial<IAppPopupOverwriteAbleOptions>)
    {
        const refs: { [K in keyof IAppPopupOverwriteAbleOptions]: Ref<IAppPopupOverwriteAbleOptions[K]> } = {
            _alwaysShowScrollBar,
            _buttons,
            _size,
            _title,
            _titleIcon,
            _textIcon,
            _textIconClass,
            _forceFocusLastElement,
        } as const;

        (Object.keys(options) as Array<keyof IAppPopupOverwriteAbleOptions>).forEach((optionKey) =>
        {
            const value = options[optionKey];

            if (value !== undefined)
            {
                if (!refs[optionKey])
                {
                    captureSilentError(new Error('Got unknown option to overwrite, ' + optionKey));
                }

                refs[optionKey].value = value;
            }
        });
    }

    const exposed = Object.assign({}, portuna, {
        componentName: 'AppPopup',
        // @deprecated use Instance.emitter directly!
        emit: portuna.emitter.emit,
        persistent: props.persistent,
        isOpen,
        close,
        open,
        scrollBottom,
        scrollTop,
        queryFocusableElements,
        focusFirstElement,
        // @deprecated use Instance.overwriteOptions!
        _alwaysShowScrollBar,
        // @deprecated use Instance.overwriteOptions!
        _buttons,
        // @deprecated use Instance.overwriteOptions!
        _size,
        // @deprecated use Instance.overwriteOptions!
        _title,
        // @deprecated use Instance.overwriteOptions!
        _titleIcon,
        // @deprecated use Instance.overwriteOptions!
        _textIcon,
        // @deprecated use Instance.overwriteOptions!
        _textIconClass,
        // @deprecated use Instance.overwriteOptions!
        _forceFocusLastElement,
        descriptionElementId,
        overwriteOptions,
    }) as IAppPopupContext;

    if (props.modelValue && !opened.value)
    {
        open();
    }

    popUpInstances.push({
        id: portuna._uid,
        popup: exposed,
    });

    props.registerPopupInstance(exposed);

    onUnmounted(() =>
    {
        removeResizeListeners();

        close();

        const popupIndex = popUpInstances.findIndex(instance => instance.id === portuna._uid);
        if (popupIndex !== -1)
        {
            popUpInstances.splice(popupIndex, 1);
        }

        if (getCountOfAllOpenPopups() <= 0)
        {
            document.documentElement.classList.remove('disable-scroll');
        }
    });

    watch(isOpen, (isOpen: boolean) =>
    {
        if (isOpen)
        {
            nextTick(() =>
            {
                uIds.value = addMediaQueryHandlers(sizes, popupContainer.value);
            });
        }
        else if (uIds.value.length)
        {
            removeResizeListeners();
        }

        if (!isOpen && getCountOfAllOpenPopups() <= 0)
        {
            document.documentElement.classList.remove('disable-scroll');
        }
    });

    watch(() => props.modelValue, (newValue: boolean) =>
    {
        if (newValue)
        {
            order.value = Date.now();

            open();
        }
        else
        {
            close();
        }
    });

    watch(() => props.alwaysShowScrollBar, (newValue: boolean) =>
    {
        _alwaysShowScrollBar.value = newValue;
    });

    watch(() => props.buttons, setButtons);

    watch(() => props.size, (newValue: PopupSize) =>
    {
        _size.value = newValue;
    });

    watch(() => props.title, (newValue: string) =>
    {
        _title.value = newValue;
    });

    watch(() => props.titleIcon, (newValue: string) =>
    {
        _titleIcon.value = newValue;
    });

    watch(() => props.textIcon, (newValue: string) =>
    {
        _textIcon.value = newValue;
    });

    watch(() => props.textIconClass, (newValue: VueCssClass) =>
    {
        _textIconClass.value = newValue;
    });

    if (props.provideEventHandler)
    {
        const SubComponentEmitter = new TinyEmitter();

        const subComponentEvents = {
            ...props.events,
            scrollTop: () =>
            {
                scrollTop();
            },
            open: () =>
            {
                open();
            },
            close: () =>
            {
                close();
            },
            setButtons: (buttons: PopupButton[]) =>
            {
                overwriteOptions({
                    '_buttons': buttons,
                });
            },
            setTitle: (title: string) =>
            {
                overwriteOptions({
                    '_title': title,
                });
            },
            setSize: (size: PopupSize) =>
            {
                overwriteOptions({
                    '_size': size,
                });
            },
            setData: (options: IAppPopupOverwriteAbleOptions) =>
            {
                overwriteOptions(options);
            },
            emit: (emitData: AppPopupEmit) =>
            {
                portuna.emitter.emit(emitData.event, emitData.data);
            },
            'lazy-load-focus': () =>
            {
                queryFocusableElements();
                focusFirstElement();
            },
        } as Record<string, Function>;

        for (let event in subComponentEvents)
        {
            if (subComponentEvents.hasOwnProperty(event))
            {
                SubComponentEmitter.on(event, subComponentEvents[event]);
            }
        }

        provide('popup-emitter', SubComponentEmitter);
    }

    defineExpose(exposed);
</script>